// The alignment: one virtualized list, whatever the view mode.
//
// A Chapter of the sample Book packs to eighty-seven Blocks and a real one
// runs longer, so only the rows in the window are in the document. Everything
// that would make that expensive is kept out: the row components are memoized,
// the diff is cached per Block (diff.ts), and this component is memoized on
// props the screen keeps stable, so typing an Instruction in the action bar
// does not touch the list at all.

import { defaultRangeExtractor, type Range, useVirtualizer } from "@tanstack/react-virtual";
import { memo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import type { PlaybackStatus } from "../../../components/audio/AudioProvider";
import { AlignmentRow, DroppedNodeRow } from "./AlignmentRow";
import type { ReviewRow } from "./rows";
import type { ViewMode } from "./viewMode";

/**
 * First guess at a row's height before it is measured. A pair of a Block and
 * its Manuscript nodes runs to about this in the sample Book; every row is
 * measured for real as it mounts, so the guess only decides how the scrollbar
 * behaves before the Author has been there.
 */
const ESTIMATED_ROW_HEIGHT = 220;

/** Rows kept mounted above and below the window, so a fast scroll has something to show. */
const OVERSCAN = 4;

export interface AlignmentListProps {
  readonly rows: readonly ReviewRow[];
  readonly mode: ViewMode;
  readonly focusedIndex: number;
  /** Bumped by the screen when the focused row should take the DOM focus, and never otherwise. */
  readonly focusNonce: number;
  readonly editingIndex: number | null;
  readonly editable: boolean;
  readonly saving: boolean;
  /** Playback of the focused Block's Sample, or null when no Voice is cast. */
  readonly sampleStatus: PlaybackStatus | null;
  readonly onFocusRow: (index: number) => void;
  readonly onEditRow: (index: number) => void;
  readonly onDraftChange?: (index: number, text: string) => void;
  readonly onCommit: (index: number, text: string, viaKeyboard: boolean) => void;
  readonly onCancelEdit: (viaKeyboard: boolean) => void;
  readonly onPlay: () => void;
}

export const AlignmentList = memo(function AlignmentList({
  rows,
  mode,
  focusedIndex,
  focusNonce,
  editingIndex,
  editable,
  saving,
  sampleStatus,
  onFocusRow,
  onEditRow,
  onCommit,
  onDraftChange,
  onCancelEdit,
  onPlay,
}: AlignmentListProps) {
  const { t } = useTranslation("review");
  const scroller = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const scrollKey = `review-scroll:${location.pathname}:${location.search}`;
  // A render counter, written into the DOM so a test can prove that typing
  // outside the list leaves it alone. Cheap enough to keep in the shipped
  // build, and it is the instrument that catches the regression.
  const renders = useRef(0);
  renders.current += 1;

  // The row being edited is mounted whatever the scroll position. The draft
  // lives in the editor's own state, and unmounting is silent: React fires no
  // blur for it, so no PATCH goes out and scrolling back would hand the Author
  // the paragraph they had just rewritten, unchanged and without a word about
  // where their text went.
  const rangeExtractor = useCallback(
    (range: Range) => {
      const indexes = defaultRangeExtractor(range);
      if (editingIndex === null || indexes.includes(editingIndex)) return indexes;
      return [...indexes, editingIndex].sort((a, b) => a - b);
    },
    [editingIndex],
  );

  const virtualizer = useVirtualizer({
    initialOffset: () => Number(sessionStorage.getItem(scrollKey) ?? 0),
    count: rows.length,
    getScrollElement: () => scroller.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: OVERSCAN,
    rangeExtractor,
    getItemKey: (index) => rows[index]?.key ?? index,
  });

  // Moving the focus is two steps, because a row outside the window has no
  // element to focus: scroll it in, then focus what mounted. Scrolling and
  // mounting take a render, so the wish is latched and the effect runs after
  // every render until the row is there — one pass when it was already
  // visible, two when the virtualizer had to bring it in.
  const wantsFocus = useRef(false);
  const lastNonce = useRef(focusNonce);
  if (lastNonce.current !== focusNonce) {
    lastNonce.current = focusNonce;
    wantsFocus.current = true;
  }

  // No dependency array on purpose: the wish is the condition, not a value
  // React can compare. The nonce is what sets it, so a poll or a mutation
  // cannot pull the focus out of the Instruction field the Author is typing in.
  useEffect(() => {
    if (!wantsFocus.current || focusedIndex < 0) return;
    virtualizer.scrollToIndex(focusedIndex, { align: "auto" });
    const row = scroller.current?.querySelector<HTMLElement>(`[data-index="${focusedIndex}"]`);
    if (!row) return;
    row.focus({ preventScroll: true });
    wantsFocus.current = false;
  });

  return (
    <div
      ref={scroller}
      onScroll={(event) => sessionStorage.setItem(scrollKey, String(event.currentTarget.scrollTop))}
      data-component="AlignmentList"
      data-renders={renders.current}
      className="min-h-0 flex-1 overflow-auto border-line border-y bg-line"
    >
      <ol
        aria-label={t("alignment.label")}
        // How many rows the Chapter has, against the handful that are mounted:
        // the pair of numbers that says the virtualization is doing its job.
        data-rows={rows.length}
        className="relative m-0 w-full list-none p-0"
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualizer.getVirtualItems().map((item) => {
          const row = rows[item.index];
          if (!row) return null;
          const focused = item.index === focusedIndex;
          return (
            <li
              key={item.key}
              data-index={item.index}
              ref={virtualizer.measureElement}
              tabIndex={focused ? 0 : -1}
              aria-current={focused ? "true" : undefined}
              aria-label={
                row.kind === "pair"
                  ? t("row.block", { index: item.index + 1, total: rows.length })
                  : t("row.dropped", { index: item.index + 1, total: rows.length })
              }
              onFocus={() => onFocusRow(item.index)}
              className={
                // An outline, not an inset ring: a ring is an inset box-shadow
                // and paints under the row's children, and the grid cells
                // inside carry an opaque background, so the ring was invisible
                // and j and k moved a cursor nobody could see.
                focused
                  ? "absolute top-0 left-0 w-full list-none outline outline-1 -outline-offset-1 outline-accent"
                  : "absolute top-0 left-0 w-full list-none outline-none"
              }
              style={{ transform: `translateY(${item.start}px)` }}
            >
              {row.kind === "pair" ? (
                <AlignmentRow
                  pair={row.pair}
                  index={item.index}
                  mode={mode}
                  focused={focused}
                  editing={editingIndex === item.index}
                  editable={editable}
                  saving={saving}
                  sampleStatus={focused ? sampleStatus : null}
                  onEdit={onEditRow}
                  onCommit={onCommit}
                  onDraftChange={onDraftChange}
                  onCancelEdit={onCancelEdit}
                  onPlay={onPlay}
                />
              ) : (
                <DroppedNodeRow node={row.node} mode={mode} />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
});
